import { ChangeDetectorRef, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { Team } from '../../../models/team';
import { TournamentGroup } from '../../../models/tournament';
import { TournamentService } from '../../../services/tournament.service';

@Component({
  standalone: false,
  selector: 'app-group-admin',
  template: "<app-loading-overlay [show]=\"isSaving || deletingGroupIds.size > 0 || generatingGroupIds.size > 0\"></app-loading-overlay>\n\n<details class=\"card accordion-card\" open>\n  <summary>\n    <span>Gestione gironi</span>\n    <small>{{ groups.length }} gironi</small>\n  </summary>\n\n  <div class=\"accordion-body\">\n    <div class=\"section-actions\">\n      <button type=\"button\" (click)=\"openCreateGroupModal()\" [disabled]=\"isSaving\">Crea girone</button>\n    </div>\n\n    <p class=\"error-message\" *ngIf=\"errorMessage\">{{ errorMessage }}</p>\n    <p class=\"success-message\" *ngIf=\"successMessage\">{{ successMessage }}</p>\n    <p class=\"loading-message\" *ngIf=\"isSaving || deletingGroupIds.size || generatingGroupIds.size\">Operazione in corso, attendere...</p>\n\n    <div class=\"calendar-options\">\n      <label>\n        Generazione calendario gironi\n        <select [(ngModel)]=\"calendarMode\" name=\"calendarMode\">\n          <option value=\"single\">Solo andata</option>\n          <option value=\"double\">Andata e ritorno</option>\n        </select>\n      </label>\n    </div>\n\n    <div class=\"group-list\">\n      <div *ngFor=\"let group of groups\" class=\"list-item\">\n        <div class=\"item-main\">\n          <strong>{{ group.name }}</strong>\n          <span>{{ group.qualifiers_count }} qualificate - {{ group.teams.length }} squadre</span>\n          <small *ngIf=\"group.three_qualifiers_bye\">Prime classificate direttamente in semifinale</small>\n          <small *ngIf=\"!group.teams.length\">Nessuna squadra assegnata</small>\n          <div class=\"chips\" *ngIf=\"group.teams.length\">\n            <span *ngFor=\"let team of group.teams\">{{ team.name }}</span>\n          </div>\n        </div>\n        <div class=\"list-actions\">\n          <button (click)=\"generateMatches(group)\" [disabled]=\"group.teams.length < 2 || isGeneratingGroup(group.id)\">\n            {{ isGeneratingGroup(group.id) ? 'Generazione...' : (calendarMode === 'double' ? 'Genera andata/ritorno' : 'Genera solo andata') }}\n          </button>\n          <button class=\"secondary\" (click)=\"editGroup(group)\" [disabled]=\"isSaving || isDeletingGroup(group.id)\">Modifica</button>\n          <button class=\"danger\" (click)=\"deleteGroup(group)\" [disabled]=\"isDeletingGroup(group.id)\">\n            {{ isDeletingGroup(group.id) ? 'Cancellazione...' : 'Elimina' }}\n          </button>\n        </div>\n      </div>\n    </div>\n  </div>\n</details>\n\n<div class=\"modal-backdrop\" *ngIf=\"groupModalOpen\">\n  <section class=\"modal-panel wide\">\n    <div class=\"modal-heading\">\n      <h2>{{ editingGroup ? 'Modifica girone' : 'Nuovo girone' }}</h2>\n      <button type=\"button\" class=\"secondary icon-button\" (click)=\"closeGroupModal()\">Chiudi</button>\n    </div>\n\n    <form class=\"form-grid\" (submit)=\"$event.preventDefault(); saveGroup();\">\n      <label>\n        Nome girone\n        <input type=\"text\" [(ngModel)]=\"groupForm.name\" name=\"groupName\" placeholder=\"Girone A\" />\n      </label>\n\n      <label>\n        Squadre qualificate\n        <input type=\"number\" [(ngModel)]=\"groupForm.qualifiers_count\" name=\"qualifiersCount\" min=\"1\" step=\"1\" />\n      </label>\n\n      <label class=\"check-row\">\n        <input type=\"checkbox\" [(ngModel)]=\"groupForm.shootouts_enabled\" name=\"shootoutsEnabled\" />\n        <span>In caso di parità, rigori (3 punti vittoria nei regolamentari; 2 alla vincente e 1 alla perdente ai rigori)</span>\n      </label>\n\n      <label class=\"check-row\" *ngIf=\"groupForm.qualifiers_count === 3\">\n        <input type=\"checkbox\" [(ngModel)]=\"groupForm.three_qualifiers_bye\" name=\"threeQualifiersBye\" />\n        <span>Fase finale con prime classificate direttamente in semifinale (2B–3A e 2A–3B ai quarti)</span>\n      </label>\n\n      <div class=\"team-picker\">\n        <label *ngFor=\"let team of teams\" class=\"check-row\">\n          <input\n            type=\"checkbox\"\n            [checked]=\"isSelected(team.id)\"\n            (change)=\"toggleTeam(team.id, $any($event.target).checked)\"\n          />\n          <span>{{ team.name }}</span>\n        </label>\n      </div>\n\n      <p class=\"error-message\" *ngIf=\"errorMessage\">{{ errorMessage }}</p>\n      <p class=\"loading-message\" *ngIf=\"isSaving\">Salvataggio in corso...</p>\n\n      <div class=\"actions\">\n        <button type=\"submit\" [disabled]=\"isSaving\">{{ isSaving ? 'Salvataggio...' : (editingGroup ? 'Aggiorna girone' : 'Crea girone') }}</button>\n        <button type=\"button\" class=\"secondary\" (click)=\"closeGroupModal()\" [disabled]=\"isSaving\">Annulla</button>\n      </div>\n    </form>\n  </section>\n</div>\n",
  styles: [".team-picker {\n  border: 1px solid rgba(255, 255, 255, 0.08);\n  border-radius: 8px;\n  display: grid;\n  gap: 0.35rem;\n  max-height: 220px;\n  overflow: auto;\n  padding: 0.75rem;\n}\n\n.check-row {\n  align-items: center;\n  display: flex;\n  flex-direction: row;\n  gap: 0.55rem;\n}\n\n.check-row input {\n  width: auto;\n}\n\n.actions,\n.chips {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.6rem;\n}\n\n.calendar-options {\n  margin-top: 1rem;\n  max-width: 280px;\n}\n\n.group-list {\n  display: grid;\n  gap: 0.75rem;\n  margin-top: 1rem;\n}\n\n.chips span {\n  background: rgba(0, 184, 63, 0.18);\n  border: 1px solid rgba(32, 219, 96, 0.24);\n  border-radius: 999px;\n  color: #b9f6c8;\n  font-size: 0.8rem;\n  padding: 0.25rem 0.55rem;\n}\n"]
})
export class GroupAdminComponent {
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly tournamentService = new TournamentService();

  @Input() teams: Team[] = [];
  @Input() groups: TournamentGroup[] = [];
  @Output() changed = new EventEmitter<void>();

  groupForm = { name: '', qualifiers_count: 2, shootouts_enabled: false, three_qualifiers_bye: false, team_ids: [] as number[] };
  calendarMode: 'single' | 'double' = 'single';
  editingGroup: TournamentGroup | null = null;
  groupModalOpen = false;
  errorMessage = '';
  successMessage = '';
  isSaving = false;
  deletingGroupIds = new Set<number>();
  generatingGroupIds = new Set<number>();

  async saveGroup(): Promise<void> {
    if (this.isSaving) {
      return;
    }

    if (this.groupForm.name.trim() === '') {
      this.errorMessage = 'Inserisci il nome del girone.';
      return;
    }
    if (this.groupForm.qualifiers_count < 1) {
      this.errorMessage = 'Il numero di qualificate deve essere almeno 1.';
      return;
    }

    try {
      this.isSaving = true;
      this.errorMessage = '';
      this.successMessage = '';
      const payload = {
        name: this.groupForm.name.trim(),
        qualifiers_count: Number(this.groupForm.qualifiers_count),
        shootouts_enabled: this.groupForm.shootouts_enabled,
        three_qualifiers_bye: this.groupForm.qualifiers_count === 3 && this.groupForm.three_qualifiers_bye,
        team_ids: this.groupForm.team_ids
      };

      if (this.editingGroup) {
        await this.tournamentService.updateGroup(this.editingGroup.id, payload);
      } else {
        await this.tournamentService.createGroup(payload);
      }

      this.groupModalOpen = false;
      this.successMessage = 'Operazione conclusa. Girone salvato.';
      this.resetForm();
      this.changed.emit();
      this.changeDetector.detectChanges();
    } catch (error) {
      this.successMessage = '';
      this.errorMessage = error instanceof Error ? error.message : 'Errore durante il salvataggio del girone.';
      this.changeDetector.detectChanges();
    } finally {
      this.isSaving = false;
      this.changeDetector.detectChanges();
    }
  }

  editGroup(group: TournamentGroup): void {
    this.editingGroup = group;
    this.groupForm = {
      name: group.name,
      qualifiers_count: group.qualifiers_count,
      shootouts_enabled: !!group.shootouts_enabled,
      three_qualifiers_bye: !!group.three_qualifiers_bye,
      team_ids: group.teams.map((team) => team.id)
    };
    this.groupModalOpen = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.changeDetector.detectChanges();
  }

  async deleteGroup(group: TournamentGroup): Promise<void> {
    if (this.deletingGroupIds.has(group.id)) {
      return;
    }

    if (!window.confirm(`Confermi la cancellazione del girone "${group.name}"?`)) {
      return;
    }

    try {
      this.deletingGroupIds.add(group.id);
      this.errorMessage = '';
      this.successMessage = '';
      await this.tournamentService.deleteGroup(group.id);
      this.resetForm();
      this.groups = this.groups.filter((item) => item.id !== group.id);
      this.successMessage = 'Operazione conclusa. Girone cancellato.';
      this.changed.emit();
      this.changeDetector.detectChanges();
    } catch (error) {
      this.successMessage = '';
      this.errorMessage = error instanceof Error ? error.message : 'Errore durante la cancellazione del girone.';
      this.changeDetector.detectChanges();
    } finally {
      this.deletingGroupIds.delete(group.id);
      this.changeDetector.detectChanges();
    }
  }

  async generateMatches(group: TournamentGroup): Promise<void> {
    if (this.generatingGroupIds.has(group.id)) {
      return;
    }

    try {
      this.generatingGroupIds.add(group.id);
      this.errorMessage = '';
      this.successMessage = '';
      await this.tournamentService.generateGroupMatches(group.id, this.calendarMode === 'double');
      this.successMessage = 'Operazione conclusa. Calendario girone generato.';
      this.changed.emit();
      this.changeDetector.detectChanges();
    } catch (error) {
      this.successMessage = '';
      this.errorMessage = error instanceof Error ? error.message : 'Errore durante la generazione degli accoppiamenti.';
      this.changeDetector.detectChanges();
    } finally {
      this.generatingGroupIds.delete(group.id);
      this.changeDetector.detectChanges();
    }
  }

  resetForm(): void {
    this.editingGroup = null;
    this.groupForm = { name: '', qualifiers_count: 2, shootouts_enabled: false, three_qualifiers_bye: false, team_ids: [] };
    this.errorMessage = '';
    this.changeDetector.detectChanges();
  }

  openCreateGroupModal(): void {
    this.resetForm();
    this.groupModalOpen = true;
    this.successMessage = '';
    this.changeDetector.detectChanges();
  }

  closeGroupModal(): void {
    this.groupModalOpen = false;
    this.resetForm();
  }

  toggleTeam(teamId: number, checked: boolean): void {
    if (checked) {
      this.groupForm.team_ids = Array.from(new Set([...this.groupForm.team_ids, teamId]));
      this.changeDetector.detectChanges();
      return;
    }

    this.groupForm.team_ids = this.groupForm.team_ids.filter((id) => id !== teamId);
    this.changeDetector.detectChanges();
  }

  isSelected(teamId: number): boolean {
    return this.groupForm.team_ids.includes(teamId);
  }

  isDeletingGroup(groupId: number): boolean {
    return this.deletingGroupIds.has(groupId);
  }

  isGeneratingGroup(groupId: number): boolean {
    return this.generatingGroupIds.has(groupId);
  }
}
